/*
 * DSD Trust & Reputation Registry — p11dz149
 * Registry-only trust foundation for future galaxy/universe creation, moderation eligibility,
 * adult/sensitive privileges, stories, chat, and creator reputation.
 * No Firebase reads/writes/realtime. No enforcement. No scoring decisions applied to UI.
 */
(function(){
  'use strict';
  if (window.DSDTrustRegistry && window.DSDTrustRegistry.version) return;

  var VERSION = 'p11dz149-trust-reputation-foundation-v1';
  var REGISTRY_OWNER = 'TrustRegistry';
  var WRITE_ENABLED = false;
  var READ_ENABLED = false;
  var REALTIME_ENABLED = false;
  var ENFORCEMENT_ENABLED = false;

  function registry(){ return window.DSDRegistry || window.DSDRuntimeRegistry || null; }
  function claim(surface, meta){
    try {
      var r = registry();
      if (r && typeof r.claim === 'function') r.claim(surface, REGISTRY_OWNER, meta || {});
      else if (r && typeof r.claimOwnership === 'function') r.claimOwnership(surface, REGISTRY_OWNER, meta || {});
    } catch(e) {}
  }
  function event(name, meta){
    try {
      var r = registry();
      if (r && typeof r.event === 'function') r.event(name, meta || {});
      else if (r && typeof r.trackEvent === 'function') r.trackEvent(name, meta || {});
    } catch(e) {}
  }

  var trustDoctrine = [
    'TrustRegistry is a governance contract, not an enforcement layer in this build.',
    'Trust score and reputation score are separate concepts.',
    'Trust controls safety eligibility; reputation controls community/creator value signals.',
    'No user-facing restrictions are applied until explicit enforcement phases.',
    'Universe/galaxy creation, admin/moderator eligibility, stories, chat, and adult/sensitive privileges must eventually consult this registry.',
    'Platform moderation and legal safety always override trust/reputation privileges.'
  ];

  var trustDimensions = {
    accountAge: { label: 'Account age', type: 'safety', launchWeight: 'future' },
    verificationState: { label: 'Verification state', type: 'safety', launchWeight: 'future' },
    reportHistory: { label: 'Report history', type: 'safety', launchWeight: 'future' },
    warningHistory: { label: 'Warning history', type: 'safety', launchWeight: 'future' },
    moderationActions: { label: 'Moderation actions', type: 'safety', launchWeight: 'future' },
    appealOutcomes: { label: 'Appeal outcomes', type: 'safety', launchWeight: 'future' },
    identityCompleteness: { label: 'Identity completeness', type: 'safety', launchWeight: 'future' },
    behaviorRisk: { label: 'Behavior risk', type: 'safety', launchWeight: 'future' },
    uploadRisk: { label: 'Upload risk', type: 'safety', launchWeight: 'future' },
    adultContentCompliance: { label: 'Adult/sensitive compliance', type: 'safety', launchWeight: 'future' }
  };

  var reputationDimensions = {
    positiveEngagement: { label: 'Positive engagement', type: 'community', launchWeight: 'future' },
    creatorRetention: { label: 'Creator retention', type: 'creator', launchWeight: 'future' },
    communityGrowth: { label: 'Community growth', type: 'community', launchWeight: 'future' },
    moderationHealth: { label: 'Moderation health', type: 'community', launchWeight: 'future' },
    eventParticipation: { label: 'Event participation', type: 'ecosystem', launchWeight: 'future' },
    galaxyStewardship: { label: 'Galaxy stewardship', type: 'ecosystem', launchWeight: 'future' },
    universeStewardship: { label: 'Universe stewardship', type: 'ecosystem', launchWeight: 'future' }
  };

  var trustStates = {
    unknown: { label: 'Unknown', description: 'No scored trust state yet.' },
    newAccount: { label: 'New Account', description: 'Account exists but lacks history.' },
    standard: { label: 'Standard', description: 'Default normal member trust state.' },
    trusted: { label: 'Trusted', description: 'Future elevated eligibility state.' },
    restricted: { label: 'Restricted', description: 'Limited privileges due to safety signals.' },
    underReview: { label: 'Under Review', description: 'Pending moderation/trust review.' },
    suspended: { label: 'Suspended', description: 'Platform-level account suspension.' },
    banned: { label: 'Banned', description: 'Platform-level ban.' }
  };

  var reputationStates = {
    none: { label: 'None', description: 'No reputation data yet.' },
    emerging: { label: 'Emerging', description: 'Early positive participation.' },
    active: { label: 'Active', description: 'Regular positive participation.' },
    established: { label: 'Established', description: 'Sustained positive impact.' },
    leader: { label: 'Leader', description: 'High-value community/creator role.' },
    declining: { label: 'Declining', description: 'Reduced activity/health.' },
    riskFlagged: { label: 'Risk Flagged', description: 'Reputation impacted by safety/community concerns.' }
  };

  var eligibilityContracts = {
    createGalaxy: {
      requiredTrust: ['standard','trusted'],
      disallowedTrust: ['restricted','underReview','suspended','banned'],
      enforcement: 'future-disabled'
    },
    createUniverse: {
      requiredTrust: ['trusted'],
      requiresApplication: true,
      enforcement: 'future-disabled'
    },
    serveAsGalaxyModerator: {
      requiredTrust: ['standard','trusted'],
      disallowedTrust: ['restricted','underReview','suspended','banned'],
      enforcement: 'future-disabled'
    },
    serveAsUniverseModerator: {
      requiredTrust: ['trusted'],
      enforcement: 'future-disabled'
    },
    postAdultSensitiveContent: {
      requiredTrust: ['standard','trusted'],
      requiresAgeGate: true,
      requiresContentRatingCompliance: true,
      enforcement: 'future-disabled'
    },
    useGalaxyChat: {
      requiredTrust: ['standard','trusted'],
      rateLimitEligible: true,
      enforcement: 'future-disabled'
    },
    createStories: {
      requiredTrust: ['standard','trusted'],
      moderationMetadataRequired: true,
      enforcement: 'future-disabled'
    }
  };

  var futurePathContracts = {
    trustProfile: 'trustProfiles/{userId}',
    trustEvents: 'trustProfiles/{userId}/events/{eventId}',
    reputationProfile: 'reputationProfiles/{userId}',
    creatorReputation: 'creatorProfiles/{userId}/reputation/current',
    galaxyTrust: 'galaxies/{galaxyId}/trust/{userId}',
    universeTrust: 'universes/{universeId}/trust/{userId}',
    moderationTrustEvents: 'moderation/trustEvents/{eventId}'
  };

  var report = {
    version: VERSION,
    owner: REGISTRY_OWNER,
    trustDoctrine: trustDoctrine,
    trustDimensions: trustDimensions,
    reputationDimensions: reputationDimensions,
    trustStates: trustStates,
    reputationStates: reputationStates,
    eligibilityContracts: eligibilityContracts,
    futurePathContracts: futurePathContracts,
    runtimeFlags: {
      firebaseReadsEnabled: READ_ENABLED,
      firebaseWritesEnabled: WRITE_ENABLED,
      realtimeEnabled: REALTIME_ENABLED,
      enforcementEnabled: ENFORCEMENT_ENABLED
    },
    dependencies: {
      identityRegistry: !!window.DSDIdentityRegistry,
      membershipRegistry: !!window.DSDMembershipRegistry,
      governanceRegistry: !!window.DSDGovernanceRegistry,
      moderationMetadata: !!window.DSDModerationMetadata,
      classificationLayer: !!window.DSDClassificationLayer,
      firebaseSocialSchema: !!window.DSDFirebaseSocialSchema
    }
  };

  window.DSDTrustRegistry = {
    version: VERSION,
    owner: REGISTRY_OWNER,
    getReport: function(){ return JSON.parse(JSON.stringify(report)); },
    getTrustStates: function(){ return JSON.parse(JSON.stringify(trustStates)); },
    getReputationStates: function(){ return JSON.parse(JSON.stringify(reputationStates)); },
    getEligibilityContracts: function(){ return JSON.parse(JSON.stringify(eligibilityContracts)); },
    evaluateEligibilityDryRun: function(action, context){
      var contract = eligibilityContracts[action] || null;
      return {
        action: action,
        contract: contract,
        context: context || {},
        allowed: null,
        reason: 'dry-run-only-no-enforcement',
        firebaseReadsEnabled: READ_ENABLED,
        enforcementEnabled: ENFORCEMENT_ENABLED
      };
    }
  };

  claim('trust-registry:doctrine', { version: VERSION, doctrine: trustDoctrine, count: trustDoctrine.length });
  claim('trust-registry:trust-dimensions', { version: VERSION, dimensions: trustDimensions, count: Object.keys(trustDimensions).length });
  claim('trust-registry:reputation-dimensions', { version: VERSION, dimensions: reputationDimensions, count: Object.keys(reputationDimensions).length });
  claim('trust-registry:trust-states', { version: VERSION, states: trustStates, count: Object.keys(trustStates).length });
  claim('trust-registry:reputation-states', { version: VERSION, states: reputationStates, count: Object.keys(reputationStates).length });
  claim('trust-registry:eligibility-contracts', { version: VERSION, contracts: eligibilityContracts, count: Object.keys(eligibilityContracts).length, enforcementEnabled: ENFORCEMENT_ENABLED });
  claim('trust-registry:path-contracts', { version: VERSION, paths: futurePathContracts });
  claim('trust-registry:firebase-firewall', { version: VERSION, firebaseReadsEnabled: READ_ENABLED, firebaseWritesEnabled: WRITE_ENABLED, realtimeEnabled: REALTIME_ENABLED, enforcementEnabled: ENFORCEMENT_ENABLED });
  event('TRUST_REGISTRY_READY', { owner: REGISTRY_OWNER, version: VERSION, enforcementEnabled: ENFORCEMENT_ENABLED });
  try { window.dispatchEvent(new CustomEvent('dsd:trust-registry-ready', { detail: report })); } catch(e) {}
})();
